import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { palette } from "@jnpmedi/grebob";
import { StyledSignIndicator } from "@newComponents/common/Common.styled";
import { appColorWarning } from "@assets/styles/colors";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { defaultMarkerColor, getColorBySignerIndex, MarkerColorType } from "../types";

import { LoLang } from "@maven-rest/common/common.types";
import { DocumentSignerAttrType, DocumentSignerSignature } from "@maven-rest/docs/DocumentSign.dto";

export const StyledMarkerZone = styled.div<{
  width: string;
  height: string;
  transform: string;
  transformOrigin: string;
  isVisible?: boolean;
  isModeLinking?: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  text-indent: -9999px;

  ${({ isModeLinking }) =>
    isModeLinking
      ? css`
          display: block;
        `
      : ""}

  ${({ width, height, transform, transformOrigin }) => ({ width, height, transform, transformOrigin })}
  ${({ isVisible }) => (isVisible ? "" : `visibility: hidden;`)}

  &:before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 3px solid ${appColorWarning};
  }
`;

export const SignatureResult = styled.div<{ url: string; width?: number; height?: number }>`
  ${({ width, height }) => ({
    width,
    height,
  })}
  background-image: url(${({ url }) => url});
  background-size: cover;
  position: relative;
`;

export const StyledMarker = styled.div<{
  lang: LoLang;
  signerIndex: number;
  markerColor?: MarkerColorType;
  signerAttrType: DocumentSignerAttrType;
  left: string;
  top: string;
  signature?: DocumentSignerSignature;
  isModeLinking: boolean;
  isModeSigning: boolean;
  isChecked: boolean;
  isSigned: boolean;
  isReadonly: boolean;
}>`
  position: absolute;
  display: flex;
  text-indent: 0;
  white-space: nowrap;
  cursor: default;
  border-radius: 2px;
  transition: background-color ease-in-out 0.1s;
  transform: translateY(-50%);
  box-sizing: border-box;
  user-select: none;

  ${({ left, top }) => ({ left, top })}

  .close-icon {
    position: absolute;
    top: calc(50% - 10px);
    right: -24px;
    text-align: center;
    cursor: pointer;
  }

  ${({ signerAttrType, isModeSigning, isSigned, markerColor, signerIndex }) =>
    isModeSigning
      ? css`
          box-sizing: border-box;
          color: ${palette["grey080"]};
          background-color: ${palette["grey020"]};
          padding: 2px 6px;

          ${signerAttrType === DocumentSignerAttrType.StampApiMark
            ? css`
                color: transparent;
                opacity: 0.8;
              `
            : ""}

          ${signerAttrType === DocumentSignerAttrType.ResponsiveItemFieldMark
            ? css`
                background: transparent;
                text-indent: -9999px;
                cursor: pointer;
                animation: none;
              `
            : ""}
        
        ${signerAttrType === DocumentSignerAttrType.Signature
            ? css`
                display: flex;
                align-items: center;
                justify-content: center;
                color: ${palette[defaultMarkerColor.sign.text]};
                border: 2px solid ${palette[defaultMarkerColor.sign.border]};
                cursor: pointer;
                width: 100px;
                height: 32px;
                background-color: ${defaultMarkerColor.sign.background};
                ${isSigned
                  ? css`
                      background: transparent;
                      text-indent: -9999px;
                      animation: none;
                      padding: 0 !important;
                    `
                  : ""}

                &:hover {
                  background-color: ${defaultMarkerColor.sign.backgroundHover};
                }

                &:after {
                  display: ${isSigned ? "none" : "block"};
                  ${StyledSignIndicator}
                }
              `
            : ""}
        `
      : css`
          .marker-label-wrapper {
            border-radius: 2px;
            min-width: 100px;
            background-color: ${markerColor?.background || getColorBySignerIndex(signerIndex).background};
            border: 2px solid ${palette[markerColor?.border || getColorBySignerIndex(signerIndex).border]};
            width: ${signerAttrType === DocumentSignerAttrType.Signature || signerAttrType === DocumentSignerAttrType.Sign
              ? baseWidth.appMarkerSignatureWidth
              : "inherit"};
            max-width: ${signerAttrType === DocumentSignerAttrType.Signature || signerAttrType === DocumentSignerAttrType.Sign
              ? "none"
              : baseWidth.appESignCommonMarkerWidth};
            height: ${signerAttrType === DocumentSignerAttrType.Signature || signerAttrType === DocumentSignerAttrType.Sign
              ? baseHeight.appMarkerSignatureHeight
              : "24px"};
            justify-content: ${signerAttrType === DocumentSignerAttrType.Signature || signerAttrType === DocumentSignerAttrType.Sign
              ? "center"
              : "flex-start"};

            & > span {
              padding: 0px 1px;
              color: ${palette[markerColor?.text || getColorBySignerIndex(signerIndex).text]};
            }
          }

          &:hover {
            background-color: ${getColorBySignerIndex(signerIndex).backgroundHover};
          }
        `}
`;
